import type { GuideBeraterStart } from './GuideBeraterChat';
import GuideBeraterSidebar from './GuideBeraterSidebar';
import { Suspense } from 'react';
import ErrorBoundary from './ErrorBoundary';
import PanelLoader from './panels/shared/PanelLoader';
import { PANEL_NAMES } from './panelConstants';
import { useAuth } from '../contexts/AuthContext';
import ViewControl from './ViewControl';
import { useState, useEffect, useCallback } from 'react';
import GuidePanel from './GuidePanel';
import GuideBeraterChat from './GuideBeraterChat';
import { CoachSpalte } from './GuideFlaeche';
import {
  LinkedInPanel, BridgeMonitor, InfisicalMonitor, QADashboard, RepoDashboard,
  WatchdogPanel, BackgroundOpsPanel, MaintenancePanel, UserInputAuditPanel,
  ArchitectureExplorer, ReportBuilder, PromptExplorer, BusinessAngelPanel,
  PrivatAngelPanel, BusinessDocsPanel, BusinessNavigatorPanel, SalesPanel, NetzwerkPanel, BusinessOverviewPanel, MyTasksPanel, FocusPanel, ActivityFeedPanel,
  GroupChatPanel, DeckAnnotatePanel, ReportAnnotatePanel,
  FeedbackPanel, TeamStatusPanel, UploadPanel, CalendarPanel, SchedulingPanel, MailPanel, PostfaecherPanel,
  ErrorMonitor, PartnerServerPanel, SandboxAngelPanel, PlatformAdmin, DeployPanel, EnergyCases, ReportCases } from './panelLazy';

// ============================================================================
// Standalone Deep-Link surface — /?panel=<value> renders ONE panel fullscreen,
// outside the project/flexlayout shell. Purpose: any global panel gets a stable
// URL that can be loaded in a browser panel (or any browser) and then driven
// headlessly via the control API:
//   POST /api/browser-panel/%23<nodeId>/navigate  {"url":"…/?panel=platform-admin"}
//   POST /api/browser-panel/%23<nodeId>/eval      {"script":"return document.title"}
//   POST /api/browser-panel/%23<nodeId>/click     {"selector":"[data-ai-id=platform-admin-mode-prod]"}
//   POST /api/browser-panel/%23<nodeId>/type|keypress
// → lets an agent open + validate a panel autonomously.
//
// Only GLOBAL panels (no project context) are deep-linkable. Project-scoped
// panels (cui, browser, notes, mission-chat, …) need the full workspace shell
// and are intentionally excluded. New global panel? Add it here AND in
// panelConstants.ts — the ToolHub footer derives its deep-link list from this map.
// ============================================================================
/*
 * DER GUIDE ZUM ANSEHEN — der Grund, warum es diesen Eintrag gibt.
 *
 * Am 07.09.2026 sind an einem Tag ZWEIMAL Klickwege im Guide gebrochen, und beide
 * Male fiel es erst Rafael auf ("ich komme auf jeden Fall nicht rein"). Der Grund
 * war nicht Unachtsamkeit, sondern dass niemand die Flaeche ansehen konnte: fuer
 * Karten-Buehnen gibt es einen Direktlink (?buehne=), fuer den Guide selbst gab es
 * keinen. Wer nichts ansehen kann, prueft am Ende nur den Typ — und der sagt nichts
 * darueber, ob ein Knopf sichtbar ist oder ins Leere greift.
 *
 * WAS DIESER BLICK BELEGT UND WAS NICHT. Er zeigt die echte Komponente mit echten
 * Daten: welche Knoepfe da sind, welche Saetze stehen, ob eine Kachel leer bleibt.
 * Er zeigt NICHT, ob ein Klick am Ende den richtigen Arbeitsbereich oeffnet — dafuer
 * fehlt hier die Umgebung (kein Projekt-Gerüst, keine Buehne darunter). Deshalb
 * sagen die Handler beim Klick genau das, statt stumm nichts zu tun: ein Knopf, der
 * scheinbar nicht reagiert, ist die naechste Fehldiagnose.
 */
function GuideZumAnsehen() {
  /*
   * `?thema=` UND `?karte=` GIBT ES NICHT MEHR — und das wird gesagt.
   *
   * Sie sprangen in die Themenansicht der Spalte, und die ist mit dem Umbau vom
   * 11.09.2026 verschwunden: Sichtbarkeit haengt am Raum der Buehne, nicht mehr am
   * Thema. Die Parameter einfach weiterzureichen haette einen Direktlink ergeben,
   * der stumm die normale Spalte zeigt — der Nachleser haette daraus geschlossen,
   * das Thema sei leer. Also: Hinweis statt Stille, mit dem Weg, der bleibt.
   */
  const sucheRoh = new URLSearchParams(window.location.search);
  const veraltet = ['thema', 'karte'].filter(k => sucheRoh.get(k));
  const [hinweis, setHinweis] = useState<string | null>(veraltet.length
    ? `?${veraltet.join('= und ?')}= wird nicht mehr bedient: die Themenansicht der Guide-Spalte `
      + 'ist am 11.09.2026 entfallen (Sichtbarkeit hängt am Raum der Bühne). Die Berater stehen '
      + 'unten in ihren Räumen, jeder mit einem Klick erreichbar.'
    : null);
  const [beraterStart, setBeraterStart] = useState<GuideBeraterStart | null>(null);
  const beraterBuehneId = beraterStart?.buehne.id ?? null;
  const [beraterChatOffen, setBeraterChatOffen] = useState(false);
  const waehleBeraterBuehne = useCallback((start: GuideBeraterStart | null) => { setBeraterStart(start); setBeraterChatOffen(false); }, []);
  const nichtHier = (was: string) => setHinweis(
    `„${was}" braucht den Arbeitsbereich und ist im Direktlink nicht ausfuehrbar. `
    + 'Dieser Blick dient dem Pruefen der Flaeche, nicht dem Bedienen.',
  );
  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
      {hinweis && (
        <div style={{ fontSize: 11, color: '#e0af68', padding: '6px 10px', borderBottom: '1px solid var(--tn-border)' }}>
          {hinweis}
        </div>
      )}
      <div style={{ flex: 1, minHeight: 0, display: 'flex' }}>
        <GuidePanel
          onOeffnenWorkspace={() => nichtHier('Arbeitsbereich öffnen')}
          onOeffnenSitzung={() => nichtHier('Chat öffnen')}
          onSchliessen={() => nichtHier('Guide schließen')}
          onBuehnenLayout={() => nichtHier('Bühne aufbauen')}
          onBeraterBuehne={waehleBeraterBuehne}
          onBeraterOeffnen={() => setBeraterChatOffen(true)}
          breitDargestellt
          startBuehne={new URLSearchParams(window.location.search).get('guide_buehne')}
        />
        {beraterBuehneId && <GuideBeraterSidebar key={beraterBuehneId} buehneId={beraterBuehneId} start={beraterStart ?? undefined} offen={beraterChatOffen} onOffenChange={setBeraterChatOffen} />}
      </div>
    </div>
  );
}

/**
 * DER COACH-CHAT ALS EIGENER DEEP-LINK.
 *
 * Rafael, 09.09.2026, dreimal: "im guide wird der ganze partner verknuepft
 * statt nur dem kleinen partner coach" / "der coach rechts soll so aussehen
 * wie der coach links" / "einfach das panel weiterleiten". `?dgr=1` haengt
 * den GANZEN Fern-Guide ein (Themen, Buehne, Tool Hub, ein zweites
 * Browser-Panel) — Rafael will genau EINE Kachel: denselben Chat wie links,
 * nur des jeweils anderen Hauses.
 *
 * `/api/guide/board` traegt den Coach schon haus-eigen ein
 * (metaKoordinator/metaKoordinatorPartner je nach `x-cui-haus`, siehe
 * guide.ts) — dieselbe Quelle, die App.tsx fuer die Spalte links benutzt.
 * Auf dem Haus geladen, dessen Coach gezeigt werden soll, ist das hier
 * einfach "der eigene Coach" — kein Cross-House-Aufruf noetig.
 *
 * ADMIN-ONLY, EXPLIZIT HIER GEPRUEFT — nicht nur ueber canAccessPanel: ein
 * Deep-Link, der einen Chat bildschirmfuellend ausliefert, ist genau die Art
 * Route, die bei einem Rollout an alle Partner zum offenen Loch wird
 * (Rafaels Trennwand-Vorgabe, 09.09. 10:28Z). canAccessPanel liesse jeden
 * User mit allowedPanels:'*' durch; dieser Chat gehoert nur Rafael/Admin.
 */
function CoachChatZumAnsehen() {
  const { authEnabled, user } = useAuth();
  const [chat, setChat] = useState<{ sessionId: string; accountId?: string } | null>(null);
  const [titel, setTitel] = useState('Coach');
  const [geladen, setGeladen] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);
  const [versuch, setVersuch] = useState(0);
  const erlaubt = authEnabled === false || (authEnabled === true && user?.role === 'admin');

  useEffect(() => {
    if (!erlaubt) return;
    let abgebrochen = false;
    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), 20_000);
    let wiederholen: ReturnType<typeof setTimeout> | undefined;
    setGeladen(false);
    fetch('/api/guide/board', { signal: controller.signal })
      .then(async r => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const d = await r.json();
        if (!d?.konfig || typeof d.konfig !== 'object') throw new Error('Antwort ohne Coach-Konfiguration');
        const coach = d.konfig.metaKoordinator;
        if (coach != null && (typeof coach.sessionId !== 'string' || !coach.sessionId)) {
          throw new Error('Coach-Zuordnung ohne Sitzungskennung');
        }
        return d;
      })
      .then(d => {
        if (abgebrochen) return;
        // /board projiziert metaKoordinator bereits auf das angefragte Haus.
        // Niemals das rohe Partner-/Dev-Konfigfeld als Ersatz auswählen.
        setChat(d.konfig.metaKoordinator ?? null);
        setTitel(d.konfig.metaKoordinator?.label || 'Coach');
        setFehler(null);
        setGeladen(true);
      })
      .catch(e => {
        if (abgebrochen) return;
        /*
         * KEIN ALTER STAND HINTER EINEM FRISCHEN FEHLER (Rafael, 17.09.2026
         * ~17:3xZ, dieselbe Regel wie bei einer verfallenen Buehne: "dann ist
         * es mir lieber wenn er 'keine buehne' sagt statt dass er einfach die
         * alte anzeigt"). Bis hierher blieb bei jeder Zeitueberschreitung der
         * zuletzt geladene Coach unter der roten Zeile sichtbar — nicht von
         * einem aktuellen Stand zu unterscheiden. `setChat(null)` macht den
         * Fehler ehrlich: lieber "nicht geladen" als eine stillschweigende
         * Leiche.
         */
        setChat(null);
        setFehler(controller.signal.aborted ? 'Zeitüberschreitung beim Laden des Coach' : `Coach konnte nicht geladen werden: ${String(e)}`);
        setGeladen(true);
        // Gleiche Staffelung wie GuideBeraterChat.tsx: ein Mensch vor dem
        // Bildschirm wartet nicht 30 feste Sekunden auf den naechsten Versuch.
        wiederholen = setTimeout(() => setVersuch(v => v + 1), Math.min(1000 * 2 ** Math.min(versuch, 3), 5000));
      })
      .finally(() => clearTimeout(timeout));
    return () => { abgebrochen = true; controller.abort(); clearTimeout(timeout); clearTimeout(wiederholen); };
  }, [erlaubt, versuch]);

  if (authEnabled === null) return <PanelLoader />;
  if (authEnabled && user?.role !== 'admin') {
    return <Centered><div style={{ fontSize: 16, color: 'var(--tn-text)' }}>Kein Zugriff auf den Coach-Chat.</div></Centered>;
  }
  if (fehler) return <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
    <div role="alert" data-ai-id="coach-chat-ladefehler" style={{ padding: 12, color: 'var(--tn-error,#f7768e)' }}>
      {fehler}. Coach nicht geladen — kein alter Stand wird stillschweigend weiter angezeigt.
      <button data-ai-id="coach-chat-retry" disabled={!geladen} onClick={() => setVersuch(v => v + 1)} style={{ marginLeft: 8 }}>
        {geladen ? 'Erneut laden' : 'Lädt …'}
      </button>
      <div style={{ fontSize: 12 }}>Bei einer Störung wird automatisch erneut versucht.</div>
    </div>
  </div>;
  if (!geladen) return <PanelLoader />;
  if (!chat) return <Centered>Noch kein Coach in diesem Haus eingetragen.</Centered>;
  // „Ansicht" steht in der Kopfzeile des Chats statt als eigene Zeile darueber (s. StandalonePanel unten).
  return <CoachSpalte chat={chat} titel={titel} kopfZusatz={<ViewControl />} />;
}

function BeraterChatZumAnsehen() {
  const { authEnabled, user } = useAuth();
  const id = new URLSearchParams(window.location.search).get('buehne');
  if (authEnabled !== false && user?.role !== 'admin') return <Centered>Dieser Chat ist nur für den Eigentümer zugänglich.</Centered>;
  if (!id) return <Centered>Keine Bühne ausgewählt.</Centered>;
  return <GuideBeraterChat buehneId={id} />;
}

export const STANDALONE_PANELS: Record<string, () => React.ReactNode> = {
  guide: () => <GuideZumAnsehen />,
  'coach-chat': () => <CoachChatZumAnsehen />,
  'berater-chat': () => <BeraterChatZumAnsehen />,
  'platform-admin': () => <PlatformAdmin />,
  'bridge-monitor': () => <BridgeMonitor />,
  'infisical-monitor': () => <InfisicalMonitor />,
  'qa-dashboard': () => <QADashboard />,
  'repo-dashboard': () => <RepoDashboard />,
  'watchdog': () => <WatchdogPanel />,
  'error-monitor': () => <ErrorMonitor />,
  'partner-server': () => <PartnerServerPanel />,
  'background-ops': () => <BackgroundOpsPanel />,
  'maintenance': () => <MaintenancePanel />,
  'input-audit': () => <UserInputAuditPanel />,
  'architecture': () => <ArchitectureExplorer />,
  'report-builder': () => <ReportBuilder />,
  'prompt-explorer': () => <PromptExplorer />,
  'business-angel': () => <BusinessAngelPanel />,
  'privat-angel': () => <PrivatAngelPanel />,
  'business-docs': () => <BusinessDocsPanel />,
  'business-navigator': () => <BusinessNavigatorPanel config={{ offen: new URLSearchParams(window.location.search).get('offen') ?? undefined }} />,
  'sales': () => <SalesPanel />,
  'netzwerk': () => <NetzwerkPanel />,
  'business-overview': () => <BusinessOverviewPanel />,
  'group-chat': () => <GroupChatPanel />,
  'deck-annotate': () => <DeckAnnotatePanel />,
  // Eigenes Fenster: der Bericht kommt aus ?reportId= (in der Buehne aus der Kachel-config).
  'report-annotate': () => <ReportAnnotatePanel reportId={new URLSearchParams(window.location.search).get('reportId') ?? undefined} />,
  'my-tasks': () => <MyTasksPanel />,
  'focus': () => <FocusPanel />,
  'activity-feed': () => <ActivityFeedPanel />,
  'energy-cases': () => <EnergyCases />,
  'report-cases': () => <ReportCases />,
  'feedback': () => <FeedbackPanel />,
  'team-status': () => <TeamStatusPanel />,
  'uploads': () => <UploadPanel />,
  'calendar': () => <CalendarPanel />,
  'scheduling': () => <SchedulingPanel />,
  'mail': () => <MailPanel />,
  'postfaecher': () => <PostfaecherPanel />,
  'linkedin': () => <LinkedInPanel />,
  'deploy': () => <DeployPanel />,
  'sandbox-rafael-angel': () => <SandboxAngelPanel mode="rafael" />,
  'sandbox-business-angel': () => <SandboxAngelPanel mode="business" />,
  'sandbox-privat-angel': () => <SandboxAngelPanel mode="private" />,
};

export function isStandalonePanel(value: string): boolean {
  return Object.prototype.hasOwnProperty.call(STANDALONE_PANELS, value);
}

function Centered({ children }: { children: React.ReactNode }) {
  return (
    <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--tn-bg)', color: 'var(--tn-text-muted)', fontSize: 13, textAlign: 'center', padding: 24 }}>
      <div>{children}</div>
    </div>
  );
}

/*
 * SCHRIFTGROESSE/ZOOM AUCH HIER (Rafael, 12.09.2026 15:12:44Z: "die schrift ist
 * ziemlich klein im guide ... das sollte eigentlich alle schriften im cui
 * betreffen").
 *
 * Die bestehende Selbstbedienungs-Skalierung (App.tsx/LayoutManager.tsx, seit
 * 04.08.2026) setzt CSS `zoom` auf den Workspace-Wrapper bzw. jedes Panel —
 * aber `?panel=`-Deep-Links (dieser Datei) haben DIESEN Wrapper nie durchlaufen:
 * App.tsx gibt bei `?panel=` frueh `<StandalonePanel>` zurueck, VOR der
 * Zoom-Huelle. Genau ueber diese Deep-Links laeuft aber ein grosser Teil dessen,
 * was Rafael taeglich ansieht — Buehnen-Kacheln (`component:"browser"`), der
 * Fern-Guide-Webview des anderen Hauses (`?panel=guide`), das Fokus-Board
 * (`?panel=focus`) — LayoutManager exemptiert `BrowserPanel` von seinem eigenen
 * Zoom sogar ausdruecklich (Electron-<webview> vertraegt kein Zoom auf einem
 * Vorfahren), gerade WEIL das Skalieren dem Dokument SELBST obliegt, nicht
 * seinem Rahmen. Dieses Dokument hier ist das.
 */
export default function StandalonePanel({ value }: { value: string }) {
  const { canAccessPanel, authEnabled, user } = useAuth();
  const render = STANDALONE_PANELS[value];
  const name = PANEL_NAMES[value] || value;

  if (!render) {
    const available = Object.keys(STANDALONE_PANELS).sort().join(', ');
    return (
      <Centered>
        <div style={{ fontSize: 16, marginBottom: 8, color: 'var(--tn-text)' }}>Kein Deep-Link für „{value}"</div>
        <div>Nur globale Panels sind als Deep-Link verfügbar (projekt-gebundene Panels brauchen den vollen Workspace).</div>
        <div style={{ marginTop: 12, fontSize: 11, opacity: 0.7, lineHeight: 1.6 }}>Verfügbar: {available}</div>
      </Centered>
    );
  }

  // Honour the same panel-access rule as the in-app factory (no-op when auth disabled).
  if (!canAccessPanel(value)) {
    return <Centered><div style={{ fontSize: 16, color: 'var(--tn-text)' }}>Kein Zugriff auf „{name}".</div></Centered>;
  }


  return (
    <div data-standalone-panel={value} data-node-id={`standalone-${value}`} style={{ height: '100%', display: 'flex', flexDirection: 'column', background: 'var(--tn-bg)', overflow: 'hidden' }}>
      {/* Der Coach-Chat traegt „Ansicht" in seiner eigenen Kopfzeile — eine Leiste weniger
          im eingebetteten Partner-Coach (Rafael, 03.10.2026). Liegt kein Chat vor
          (Fehler, Laden), fehlt sie dort; das ist nur die Dichte-Einstellung. */}
      {window.self === window.top && value !== 'coach-chat' && <div style={{ flexShrink: 0, display: 'flex', justifyContent: 'flex-end', padding: 3 }}><ViewControl /></div>}
      <ErrorBoundary componentName={`Standalone:${value}`}>
        <Suspense fallback={<PanelLoader />}>{render()}</Suspense>
      </ErrorBoundary>
    </div>
  );
}
